import React, { useEffect, useMemo, useState } from 'react';
import './ShareProjectDialog.scss';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import type { ProjectMember } from '@brand24-original/llm-types';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { getProjectMembers, shareProject } from '../../../../shared/services/team/TeamService';
import { memberDisplayName, projectGuests } from '../../agencyHubRows';
import { AGENCY_HUB_PROJECT_MEMBERS_QUERY, AGENCY_HUB_TEAM_QUERY } from '../../agencyHubQueryKeys';

interface ShareProjectDialogProps {
  /** The project being shared; null keeps the dialog closed. */
  project: { id: number; name: string } | null;
  onHide: () => void;
}

/** The backend's typed rejection, when there is one. */
const errorCode = (error: unknown): string | undefined =>
  (error as { response?: { data?: { error?: string } } }).response?.data?.error;

/**
 * "Share project": which guests see one project. The list is `GET /projects/:id/members` (every
 * member, fetched on every open) narrowed here to `scope === 'PROJECT'` — the roles that see every
 * project have nothing to decide and stay out of the dialog (decision 2026-10-22). A guest whose only
 * project this is stays checked and locked, because the backend refuses to leave a guest with no
 * project. The checks are a draft until Save sends them as `PUT /projects/:id/share`; a success
 * closes the dialog and refreshes the team list (the guests' project chips), a failure keeps the
 * draft and shows why.
 */
const ShareProjectDialog: React.FC<ShareProjectDialogProps> = ({ project, onHide }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const visible = project !== null;

  // The project the content is built for outlives the prop by one close: the parent nulls `project`
  // before PrimeReact plays the closing fade, and switching the query key and the title at that
  // moment would empty the dialog while it is still on screen. A fresh open resets the draft.
  const [lastShown, setLastShown] = useState(project);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (project) {
      setLastShown(project);
      setChecked({});
      setSaveError(null);
    }
  }, [project]);
  const shown = project ?? lastShown;
  const membersKey = [AGENCY_HUB_PROJECT_MEMBERS_QUERY, shown?.id];

  const { data: members, isError } = useQuery({
    queryKey: membersKey,
    queryFn: () => getProjectMembers(shown?.id as number),
    enabled: visible,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const guests = useMemo(() => projectGuests(members ?? []), [members]);

  // The guests' checks are a draft over the server's `assigned` (the fallback below): reset whenever
  // a fresh list arrives, written nowhere until Save.
  useEffect(() => {
    setChecked({});
  }, [guests]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: (userIds: number[]) => shareProject(shown?.id as number, userIds),
    onSuccess: () => {
      // The members list is stale for the next open; the team section's chips refresh right away.
      void queryClient.invalidateQueries({ queryKey: membersKey });
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      onHide();
    },
    onError: (error: unknown) => {
      const code = errorCode(error);
      setSaveError(code === 'GUEST_NEEDS_PROJECT' ? t('guestNeedsProject') : t('somethingWentWrong'));
      // A typed rejection means the table moved under the draft (another admin, another tab): show the
      // current state, including a freshly locked "only project". Anything else (network, 500) keeps
      // the draft so the user can simply retry.
      if (code === 'GUEST_NEEDS_PROJECT' || code === 'NOT_A_GUEST') {
        void queryClient.invalidateQueries({ queryKey: membersKey });
      }
    },
  });

  const isChecked = (guest: ProjectMember) => checked[guest.id] ?? guest.assigned;
  const isLocked = (guest: ProjectMember) => guest.assigned && guest.grantedProjectCount === 1;
  const toggle = (guest: ProjectMember) => {
    if (isLocked(guest)) return;
    setChecked((prev) => ({ ...prev, [guest.id]: !isChecked(guest) }));
  };
  const changed = guests.some((guest) => isChecked(guest) !== guest.assigned);

  const header = (
    <div className="share-project-dialog__header">
      <span>{t('shareProject')}</span>
      <span className="share-project-dialog__project" title={shown?.name}>
        {shown?.name}
      </span>
    </div>
  );

  const footer = (
    <>
      <Button className="btn btn--secondary" label={t('cancel')} onClick={onHide} />
      <Button
        className="btn btn--primary"
        label={t('save')}
        disabled={!changed || isPending}
        loading={isPending}
        onClick={() => {
          setSaveError(null);
          save(guests.filter(isChecked).map((guest) => guest.id));
        }}
      />
    </>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      footer={footer}
      className="dialog-box share-project-dialog"
      draggable={false}
    >
      {isError && <p className="share-project-dialog__error">{t('somethingWentWrong')}</p>}
      {members && (
        <>
          <p className="share-project-dialog__hint">{t('shareProjectHint')}</p>
          {guests.length === 0 ? (
            <p className="share-project-dialog__empty">{t('shareProjectNoGuests')}</p>
          ) : (
            <div className="share-project-dialog__list">
              {guests.map((guest) => {
                const locked = isLocked(guest);
                const name = memberDisplayName(guest);
                return (
                  // The whole row toggles (as the checkbox select does); the checkbox itself has no
                  // handler, so one click is one toggle.
                  <div
                    key={guest.id}
                    className={`share-project-dialog__row${
                      locked ? ' share-project-dialog__row--locked' : ''
                    }`}
                    onClick={() => toggle(guest)}
                  >
                    <Checkbox checked={isChecked(guest)} disabled={locked} />
                    <div className="share-project-dialog__member">
                      <span className="share-project-dialog__name" title={name}>
                        {name}
                      </span>
                      {name !== guest.email && (
                        <span className="share-project-dialog__email" title={guest.email}>
                          {guest.email}
                        </span>
                      )}
                    </div>
                    {locked && (
                      <>
                        <span className="share-project-dialog__only" id={`share-only-${guest.id}`}>
                          {t('guestOnlyProject')}
                        </span>
                        <CustomTooltip
                          tooltipText="guestOnlyProjectHint"
                          targetRef={`share-only-${guest.id}`}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {saveError && <p className="share-project-dialog__error">{saveError}</p>}
        </>
      )}
    </Dialog>
  );
};

export default ShareProjectDialog;
